import React, { useEffect, useState, useCallback } from 'react';
import {
  Megaphone, Plus, Sparkles, Loader2, Trash2, ArrowLeft, DollarSign, Target,
  ListTodo, Link2, QrCode, Film, Mail, FileText, TrendingUp, Calendar, Users
} from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, Stat, inputCls, copyText } from './shared';
import { toast } from 'sonner';

const OBJECTIVES = ['awareness', 'traffic', 'leads', 'sales', 'launch', 'retention', 'event'];
const CHANNELS = ['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin', 'email', 'sms', 'google_ads', 'influencer', 'print', 'events', 'pr'];
const COLUMNS = [
  { id: 'backlog', label: 'Backlog' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'review', label: 'In Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'done', label: 'Done' },
];

export default function CampaignsPanel({ setActiveSection, onCampaignsChange }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: '', objective: 'leads', brief: '', budget: '', goal_metric: 'leads', goal_target: '',
    channels: [], audience: '', start_date: '', end_date: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    suiteAPI.listCampaigns().then(r => { setCampaigns(r.data); onCampaignsChange?.(r.data); }).finally(() => setLoading(false));
  }, [onCampaignsChange]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Name your campaign');
    setSaving(true);
    try {
      const { data } = await suiteAPI.createCampaign({
        ...form, budget: Number(form.budget || 0), goal_target: Number(form.goal_target || 0),
      });
      toast.success('Campaign created');
      setShowNew(false);
      setForm({ name: '', objective: 'leads', brief: '', budget: '', goal_metric: 'leads', goal_target: '', channels: [], audience: '', start_date: '', end_date: '' });
      load();
      setOpenId(data.id);
    } catch { toast.error('Could not create campaign'); }
    finally { setSaving(false); }
  };

  if (openId) return <CampaignDetail id={openId} onBack={() => { setOpenId(null); load(); }} setActiveSection={setActiveSection} />;

  return (
    <Panel
      title="Campaigns"
      subtitle="Every campaign lives here: strategy, budget, tasks, creative, channels and results in one place."
      testId="suite-campaigns"
      action={<Button onClick={() => setShowNew(v => !v)} className="bg-[#b8ff00] text-black font-bold" data-testid="campaign-new-btn"><Plus size={15} className="mr-1.5" /> New Campaign</Button>}
    >
      {showNew && (
        <Card className="p-5 mb-5 space-y-4" data-testid="campaign-new-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Campaign name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Q3 Streetwear Launch" className={inputCls} data-testid="campaign-name" /></Field>
            <Field label="Audience"><Input value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} placeholder="18-30 urban sneaker collectors, US metros" className={inputCls} data-testid="campaign-audience" /></Field>
          </div>
          <Field label="Objective">
            <div className="flex flex-wrap gap-1.5">
              {OBJECTIVES.map(o => <Chip key={o} active={form.objective === o} onClick={() => setForm({ ...form, objective: o })} testId={`objective-${o}`}>{o}</Chip>)}
            </div>
          </Field>
          <Field label="Brief" hint="The more detail here, the better the AI plan.">
            <Textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} rows={3} placeholder="We're launching a limited drop of 200 jackets in September, want 500 pre-orders, hype driven by creators." className={inputCls} data-testid="campaign-brief" />
          </Field>
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Budget ($)"><Input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} className={inputCls} data-testid="campaign-budget" /></Field>
            <Field label="Goal metric">
              <select value={form.goal_metric} onChange={e => setForm({ ...form, goal_metric: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
                {['leads', 'conversions', 'clicks', 'revenue', 'impressions'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Start"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="campaign-start" /></Field>
            <Field label="End"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Channels">
            <div className="flex flex-wrap gap-1.5">
              {CHANNELS.map(c => (
                <Chip key={c} active={form.channels.includes(c)} testId={`channel-${c}`}
                  onClick={() => setForm(f => ({ ...f, channels: f.channels.includes(c) ? f.channels.filter(x => x !== c) : [...f.channels, c] }))}>
                  {c.replace('_', ' ')}
                </Chip>
              ))}
            </div>
          </Field>
          <Button onClick={create} disabled={saving} className="bg-[#b8ff00] text-black font-bold" data-testid="campaign-create-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />} Create campaign
          </Button>
        </Card>
      )}

      {loading ? <PanelLoader /> : campaigns.length === 0 ? (
        <EmptyState icon={Megaphone} text="No campaigns yet. Create one and let AI build the whole plan for you." />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {campaigns.map(c => (
            <Card key={c.id} className="p-4 hover:border-zinc-700 transition-colors cursor-pointer" onClick={() => setOpenId(c.id)} data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-white truncate">{c.name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${
                  c.status === 'active' ? 'bg-[#b8ff00]/15 text-[#b8ff00]' :
                  c.status === 'completed' ? 'bg-blue-500/15 text-blue-400' : 'bg-zinc-800 text-zinc-400'}`}>{c.status}</span>
              </div>
              <p className="text-[11px] text-zinc-500 capitalize mt-0.5">{c.objective} · ${Number(c.budget).toLocaleString()} budget</p>
              <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{c.brief || 'No brief yet'}</p>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-500">
                <span className="flex items-center gap-1"><ListTodo size={11} /> {c.tasks_done}/{c.task_count}</span>
                <span className="flex items-center gap-1"><FileText size={11} /> {c.asset_count} assets</span>
                {c.plan && <span className="text-[#b8ff00] flex items-center gap-1"><Sparkles size={11} /> plan ready</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Panel>
  );
}

function CampaignDetail({ id, onBack, setActiveSection }) {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState('plan');
  const [busy, setBusy] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [spend, setSpend] = useState({ amount: '', channel: 'instagram', impressions: '', clicks: '', conversions: '', revenue: '', note: '' });
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(() => {
    suiteAPI.getCampaign(id).then(r => setC(r.data));
    suiteAPI.campaignAnalytics(id).then(r => setAnalytics(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <PanelLoader />;

  const genPlan = async () => {
    setBusy('plan');
    try {
      await suiteAPI.aiCampaignPlan(id, { materialize: true });
      toast.success('Plan generated — tasks and calendar filled in');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Plan generation failed'); }
    finally { setBusy(''); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    await suiteAPI.createTask({ campaign_id: id, title: taskTitle });
    setTaskTitle('');
    load();
  };

  const moveTask = async (task, status) => {
    await suiteAPI.updateTask(task.id, { status });
    load();
  };

  const logSpend = async () => {
    if (!spend.amount) return toast.error('Enter an amount');
    await suiteAPI.logSpend(id, spend);
    setSpend({ ...spend, amount: '', impressions: '', clicks: '', conversions: '', revenue: '', note: '' });
    toast.success('Results logged');
    load();
  };

  const setStatus = async (status) => {
    await suiteAPI.updateCampaign(id, { status });
    load();
  };

  const plan = c.plan;
  const TABS = ['plan', 'tasks', 'assets', 'results'];

  return (
    <Panel
      title={c.name}
      subtitle={`${c.objective} · $${Number(c.budget).toLocaleString()} budget · ${c.start_date || 'no start'} → ${c.end_date || 'open'}`}
      testId="campaign-detail"
      action={
        <div className="flex gap-2">
          <Button onClick={onBack} className="bg-zinc-800 text-white" data-testid="campaign-back"><ArrowLeft size={14} className="mr-1.5" /> Back</Button>
          {c.status !== 'active' && <Button onClick={() => setStatus('active')} className="bg-[#b8ff00] text-black font-bold" data-testid="campaign-activate">Activate</Button>}
          {c.status === 'active' && <Button onClick={() => setStatus('completed')} className="bg-zinc-800 text-white">Mark complete</Button>}
        </div>
      }
    >
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <Stat label="Spend" value={`$${analytics.totals.spend.toLocaleString()}`} sub={`${analytics.budget_used_pct}% of budget`} />
          <Stat label="Revenue" value={`$${analytics.totals.revenue.toLocaleString()}`} tone="text-[#b8ff00]" sub={`ROAS ${analytics.totals.roas}x`} />
          <Stat label="Clicks" value={analytics.totals.total_clicks} />
          <Stat label="Leads" value={analytics.totals.leads} tone="text-cyan-400" sub={`CPL $${analytics.totals.cpl}`} />
          <Stat label="Conversions" value={analytics.totals.conversions} />
          <Stat label="Goal" value={`${analytics.goal_progress}%`} sub={`${c.goal_target} ${c.goal_metric}`} />
        </div>
      )}

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {TABS.map(t => <Chip key={t} active={tab === t} onClick={() => setTab(t)} testId={`campaign-tab-${t}`}>{t}</Chip>)}
      </div>

      {tab === 'plan' && (
        <div className="space-y-4">
          {!plan ? (
            <Card className="p-8 text-center">
              <Sparkles size={30} className="mx-auto mb-3 text-[#b8ff00]" />
              <p className="text-white font-semibold mb-1">Let AI build the entire campaign</p>
              <p className="text-zinc-500 text-sm mb-4 max-w-lg mx-auto">Strategy, positioning, channel plan with budget splits, funnel, a full task list, a content calendar, copy angles and video ad briefs — generated from your brief and written straight into this campaign.</p>
              <Button onClick={genPlan} disabled={busy === 'plan'} className="bg-[#b8ff00] text-black font-bold" data-testid="campaign-ai-plan">
                {busy === 'plan' ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Building plan…</> : <><Sparkles size={15} className="mr-1.5" /> Generate campaign plan</>}
              </Button>
            </Card>
          ) : (
            <>
              <Card className="p-5">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Strategy</p>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{plan.strategy}</p>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-4 mb-1">Key message</p>
                <p className="text-sm text-[#b8ff00] font-medium">{plan.key_message}</p>
                {plan.positioning && <><p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-4 mb-1">Positioning</p><p className="text-sm text-zinc-300">{plan.positioning}</p></>}
              </Card>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-3">Channel plan</p>
                  {(plan.channels || []).map((ch, i) => (
                    <div key={i} className="py-2 border-b border-zinc-800 last:border-0">
                      <div className="flex justify-between">
                        <p className="text-xs font-semibold text-white capitalize">{ch.channel}</p>
                        <span className="text-[10px] text-[#b8ff00]">{ch.budget_pct}% budget</span>
                      </div>
                      <p className="text-[11px] text-zinc-500">{ch.why}</p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">{ch.content_plan}</p>
                    </div>
                  ))}
                </Card>
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-3">Funnel</p>
                  {Object.entries(plan.funnel || {}).map(([k, v]) => (
                    <div key={k} className="py-1.5 border-b border-zinc-800 last:border-0">
                      <p className="text-[11px] uppercase tracking-wide text-zinc-500">{k}</p>
                      <p className="text-xs text-zinc-300">{v}</p>
                    </div>
                  ))}
                  <p className="text-sm font-bold text-white mt-4 mb-2">KPIs</p>
                  {(plan.kpis || []).map((k, i) => <p key={i} className="text-xs text-zinc-400">· {k.metric}: <span className="text-white">{k.target}</span></p>)}
                </Card>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-2">Copy angles</p>
                  {(plan.copy_angles || []).map((a, i) => <p key={i} className="text-xs text-zinc-400 py-1 border-b border-zinc-800 last:border-0">{a}</p>)}
                  <Button size="sm" onClick={() => setActiveSection('ai-copy')} className="mt-3 w-full bg-zinc-800 text-white text-xs">Write copy</Button>
                </Card>
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-2">Video ad briefs</p>
                  {(plan.video_ad_briefs || []).map((a, i) => <p key={i} className="text-xs text-zinc-400 py-1 border-b border-zinc-800 last:border-0">{a}</p>)}
                  <Button size="sm" onClick={() => setActiveSection('video-ads')} className="mt-3 w-full bg-zinc-800 text-white text-xs"><Film size={12} className="mr-1" /> Make video ads</Button>
                </Card>
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-2">Quick wins</p>
                  {(plan.quick_wins || []).map((a, i) => <p key={i} className="text-xs text-[#b8ff00] py-1 border-b border-zinc-800 last:border-0">{a}</p>)}
                  <p className="text-sm font-bold text-white mt-4 mb-2">Risks</p>
                  {(plan.risks || []).map((a, i) => <p key={i} className="text-xs text-amber-400/80 py-1">{a}</p>)}
                </Card>
              </div>
              <Button onClick={genPlan} disabled={busy === 'plan'} className="bg-zinc-800 text-white" data-testid="campaign-replan">
                {busy === 'plan' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Regenerate plan
              </Button>
            </>
          )}
        </div>
      )}

      {tab === 'tasks' && (
        <div>
          <div className="flex gap-2 mb-4 max-w-lg">
            <Input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder="Add a task…" className={inputCls} data-testid="task-title" onKeyDown={e => e.key === 'Enter' && addTask()} />
            <Button onClick={addTask} className="bg-[#b8ff00] text-black font-bold" data-testid="task-add"><Plus size={14} /></Button>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            {COLUMNS.map(col => (
              <div key={col.id} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid={`kanban-${col.id}`}>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{col.label} ({c.tasks.filter(t => t.status === col.id).length})</p>
                <div className="space-y-2">
                  {c.tasks.filter(t => t.status === col.id).map(t => (
                    <div key={t.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5" data-testid={`task-${t.id}`}>
                      <p className="text-xs text-white leading-snug">{t.title}</p>
                      {t.description && <p className="text-[10px] text-zinc-500 mt-1 line-clamp-2">{t.description}</p>}
                      <div className="flex items-center gap-1 mt-2">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${t.priority === 'high' ? 'bg-red-500/15 text-red-400' : 'bg-zinc-800 text-zinc-500'}`}>{t.priority}</span>
                        {t.week ? <span className="text-[9px] text-zinc-600">wk {t.week}</span> : null}
                      </div>
                      <select value={t.status} onChange={e => moveTask(t, e.target.value)} className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 px-1 py-1" data-testid={`task-move-${t.id}`}>
                        {COLUMNS.map(cc => <option key={cc.id} value={cc.id}>{cc.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'assets' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { l: 'Designs & assets', v: c.assets.length, icon: FileText, nav: 'design' },
              { l: 'Video ads', v: c.video_ads.length, icon: Film, nav: 'video-ads' },
              { l: 'Emails', v: c.emails.length, icon: Mail, nav: 'email' },
              { l: 'Social posts', v: c.social_posts.length, icon: Users, nav: 'social' },
            ].map(x => (
              <button key={x.l} onClick={() => setActiveSection(x.nav)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-left hover:border-zinc-700">
                <x.icon size={15} className="text-[#b8ff00] mb-1.5" />
                <p className="text-lg font-bold text-white leading-none">{x.v}</p>
                <p className="text-[11px] text-zinc-500">{x.l}</p>
              </button>
            ))}
          </div>
          {c.assets.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {c.assets.map(a => (
                <div key={a.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                  {(a.content_type || '').startsWith('image/')
                    ? <img src={suiteAPI.fileUrl(a.id)} alt="" className="w-full aspect-square object-cover" />
                    : <div className="w-full aspect-square flex items-center justify-center text-zinc-600"><FileText size={20} /></div>}
                  <p className="text-[10px] text-zinc-500 truncate p-1.5">{a.name}</p>
                </div>
              ))}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4">
              <p className="text-sm font-bold text-white mb-2 flex items-center gap-1.5"><Link2 size={13} /> Tracked links</p>
              {c.links.length === 0 ? <p className="text-xs text-zinc-500">No links yet.</p> : c.links.map(l => (
                <div key={l.id} className="py-1.5 border-b border-zinc-800 last:border-0 flex justify-between gap-2">
                  <p className="text-xs text-zinc-300 truncate">{l.title}</p>
                  <span className="text-[10px] text-[#b8ff00]">{l.clicks} clicks</span>
                </div>
              ))}
            </Card>
            <Card className="p-4">
              <p className="text-sm font-bold text-white mb-2 flex items-center gap-1.5"><QrCode size={13} /> QR codes</p>
              {c.qr_codes.length === 0 ? <p className="text-xs text-zinc-500">No QR codes yet.</p> : c.qr_codes.map(q => (
                <div key={q.id} className="py-1.5 border-b border-zinc-800 last:border-0 flex justify-between gap-2">
                  <p className="text-xs text-zinc-300 truncate">{q.name}</p>
                  <span className="text-[10px] text-[#b8ff00]">{q.scans} scans</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {tab === 'results' && (
        <div className="grid lg:grid-cols-[380px_1fr] gap-5">
          <Card className="p-5 space-y-3 h-fit">
            <p className="text-sm font-bold text-white flex items-center gap-1.5"><DollarSign size={14} /> Log spend & results</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount ($)"><Input type="number" value={spend.amount} onChange={e => setSpend({ ...spend, amount: e.target.value })} className={inputCls} data-testid="spend-amount" /></Field>
              <Field label="Channel">
                <select value={spend.channel} onChange={e => setSpend({ ...spend, channel: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="spend-channel">
                  {CHANNELS.map(ch => <option key={ch} value={ch}>{ch.replace('_', ' ')}</option>)}
                </select>
              </Field>
              <Field label="Impressions"><Input type="number" value={spend.impressions} onChange={e => setSpend({ ...spend, impressions: e.target.value })} className={inputCls} /></Field>
              <Field label="Clicks"><Input type="number" value={spend.clicks} onChange={e => setSpend({ ...spend, clicks: e.target.value })} className={inputCls} /></Field>
              <Field label="Conversions"><Input type="number" value={spend.conversions} onChange={e => setSpend({ ...spend, conversions: e.target.value })} className={inputCls} /></Field>
              <Field label="Revenue ($)"><Input type="number" value={spend.revenue} onChange={e => setSpend({ ...spend, revenue: e.target.value })} className={inputCls} data-testid="spend-revenue" /></Field>
            </div>
            <Field label="Note"><Input value={spend.note} onChange={e => setSpend({ ...spend, note: e.target.value })} className={inputCls} /></Field>
            <Button onClick={logSpend} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="spend-log-btn">Log entry</Button>
          </Card>
          <div className="space-y-4">
            {analytics && (
              <>
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><Target size={13} /> Funnel</p>
                  {analytics.funnel.map(f => {
                    const max = Math.max(...analytics.funnel.map(x => x.value), 1);
                    return (
                      <div key={f.stage} className="mb-2">
                        <div className="flex justify-between text-[11px] mb-1"><span className="text-zinc-400">{f.stage}</span><span className="text-white">{f.value.toLocaleString()}</span></div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-[#b8ff00]" style={{ width: `${(f.value / max) * 100}%` }} /></div>
                      </div>
                    );
                  })}
                </Card>
                <Card className="p-5">
                  <p className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><TrendingUp size={13} /> Lead sources</p>
                  {analytics.attribution.length === 0 ? <p className="text-xs text-zinc-500">No leads attributed yet.</p> : analytics.attribution.map(a => (
                    <div key={a.source} className="flex justify-between py-1 border-b border-zinc-800 last:border-0">
                      <span className="text-xs text-zinc-400 truncate">{a.source}</span><span className="text-xs text-white">{a.leads}</span>
                    </div>
                  ))}
                </Card>
                <div className="flex gap-2 flex-wrap">
                  <a href={suiteAPI.exportCsvUrl('spend', id)} target="_blank" rel="noreferrer"><Button size="sm" className="bg-zinc-800 text-white text-xs">Export spend CSV</Button></a>
                  <a href={suiteAPI.exportCsvUrl('leads', id)} target="_blank" rel="noreferrer"><Button size="sm" className="bg-zinc-800 text-white text-xs">Export leads CSV</Button></a>
                  <Button size="sm" onClick={() => setActiveSection('analytics')} className="bg-zinc-800 text-white text-xs"><Calendar size={12} className="mr-1" /> Full report</Button>
                  <Button size="sm" className="bg-zinc-800 text-white text-xs" data-testid="campaign-share" onClick={async () => {
                    const { data } = await suiteAPI.createShare({ scope: 'campaign', campaign_id: id, title: `${c.name} — Performance Report`, expires_days: 60 });
                    copyText(`${window.location.origin}/report/${data.token}`);
                    toast.success('Client report link copied — no login needed to view');
                  }}>Share with client</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
